export type WidgetPreference = {
  id: string;
  visible: boolean;
};

export type StoredWidgetPreferences = {
  version?: number;
  widgets?: WidgetPreference[];
};

export type WidgetRegistryEntry = {
  id: string;
};

/**
 * Merges stored dashboard widget preferences with the current registry.
 * Drops unknown ids, removes duplicates, and appends new widgets as visible.
 */
export const normalizeWidgetPreferences = (
  stored: StoredWidgetPreferences | null | undefined,
  registry: WidgetRegistryEntry[],
): WidgetPreference[] => {
  const registryIds = registry.map(({ id }) => id);
  const registryIdSet = new Set(registryIds);

  if (!stored || !Array.isArray(stored.widgets)) {
    return registryIds.map((id) => ({ id, visible: true }));
  }

  const seen = new Set<string>();
  const normalized: WidgetPreference[] = [];

  for (const widget of stored.widgets) {
    if (
      !widget ||
      typeof widget.id !== "string" ||
      !registryIdSet.has(widget.id) ||
      seen.has(widget.id)
    ) {
      continue;
    }

    seen.add(widget.id);
    normalized.push({
      id: widget.id,
      visible: widget.visible ?? true,
    });
  }

  for (const id of registryIds) {
    if (!seen.has(id)) {
      normalized.push({ id, visible: true });
    }
  }

  return normalized;
};
